/**
 * Lazy Zone.js loader.
 *
 * Angular MFEs (myteams, etc.) require Zone.js to be loaded into the page
 * BEFORE `bootstrapModule()` runs. Without it the platform throws NG0908
 * (`Angular requires Zone.js`). Our React host has no reason to ship Zone in
 * its own bundle (zone.js patches global async APIs and is dead weight for
 * React), so we lazy-load it the first time an Angular MFE asks for it.
 *
 * Resolution order:
 *   1. `import.meta.env.VITE_ZONE_JS_URL` (full URL, lets ops override).
 *   2. same-origin public asset, pinned to match what Angular MFEs share.
 *
 * Loaded once per page; subsequent calls return the same promise.
 */
const DEFAULT_URL = '/legacy-vendor/zone.umd.min.js';

let zonePromise: Promise<void> | null = null;

declare global {
  interface Window {
    Zone?: unknown;
  }
}

export function ensureZoneJs(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Zone) return Promise.resolve();
  if (zonePromise) return zonePromise;

  const url = (import.meta.env.VITE_ZONE_JS_URL as string | undefined)?.trim() || DEFAULT_URL;

  zonePromise = new Promise<void>((resolve, reject) => {
    // If a `<script src=...>` for the same URL already exists, hook onto it.
    const existing = document.querySelector<HTMLScriptElement>(`script[data-zone-js="true"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error(`zone.js failed to load from ${existing.src}`)),
      );
      // If it already finished before we attached the listener, resolve.
      if (window.Zone) resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = false; // execute before any subsequent script
    script.setAttribute('data-zone-js', 'true');
    script.onload = (): void => resolve();
    script.onerror = (): void => reject(new Error(`zone.js failed to load from ${url}`));
    document.head.appendChild(script);
  });
  return zonePromise;
}
